import type { EntryKey, UseQueryReturn } from "@pinia/colada";
import type { MaybeRefOrGetter, Ref } from "vue";
import type {
  UsageAggregatedParams,
  UsageDetailsFilter,
  UsageSummaryParams,
} from "@/schemas/api-keys-usage.schema";
import type { ApiKeyBody, RotateApiKeyBody } from "@/schemas/api-keys.schema";
import { defineQuery, useMutation, useQuery, useQueryCache } from "@pinia/colada";
import { ref, toValue } from "vue";
import { apiKeysApi } from "@/api/api-keys.api";
import { SUBSCRIPTION_LIMITS_QUERY_KEYS } from "@/queries/subscription-limits.query";
import { useProjectStore } from "@/stores/project.store";

export const API_KEYS_QUERY_KEYS = {
  root: ["api-keys"] as const,
  list: (projectId?: string | null) => [...API_KEYS_QUERY_KEYS.root, "list", { projectId }] as const,
  orgList: (projectId?: string | null) => [...API_KEYS_QUERY_KEYS.root, "org-list", { projectId }] as const,
  listForUser: (userId: string, projectId?: string | null) =>
    [...API_KEYS_QUERY_KEYS.root, "list-for-user", userId, { projectId }] as const,

  // Usage endpoints
  usageSummaryById: (id: string, params: UsageSummaryParams = {}) =>
    [...API_KEYS_QUERY_KEYS.root, id, "usage", "summary", params] as const,

  // Usage endpoints
  usageSummary: (params: UsageSummaryParams = {}) =>
    [...API_KEYS_QUERY_KEYS.root, "usage", "summary", params] as const,

  usageDetails: (id: string, filter?: UsageDetailsFilter) =>
    [...API_KEYS_QUERY_KEYS.root, id, "usage", "details", filter] as const,

  usageAggregated: (id: string, params?: UsageAggregatedParams) =>
    [...API_KEYS_QUERY_KEYS.root, id, "usage", "aggregated", params] as const,
};

/**
 * Query hook for fetching API keys
 *
 * Features:
 * - **Project filtering**: Automatically filters by selected project via X-Project-ID header
 * - Smart caching with 5min stale time
 * - Query refetches when selected project changes
 */
export function useApiKeysQuery() {
  const projectStore = useProjectStore();

  return useQuery({
    // Include project ID in key so query refetches when project changes
    key: () => API_KEYS_QUERY_KEYS.list(projectStore.currentProjectId) as unknown as EntryKey,
    query: async () => {
      // Pass project header to filter by selected project
      return await apiKeysApi.get({
        headers: projectStore.getProjectHeader(),
      });
    },
    staleTime: 5 * 60_000,
    gcTime: 10 * 60_000,
  });
}

/**
 * Query hook for fetching all organization API keys visible to the current user.
 * Organization-scope users see all org keys; others see only their own.
 *
 * Used by dashboard/analytics components to resolve API key names across the org.
 */
export function useOrgApiKeysQuery() {
  const projectStore = useProjectStore();

  return useQuery({
    key: () => API_KEYS_QUERY_KEYS.orgList(projectStore.currentProjectId) as unknown as EntryKey,
    query: async () => {
      return await apiKeysApi.getOrganizationKeys({
        headers: projectStore.getProjectHeader(),
      });
    },
    staleTime: 5 * 60_000,
    gcTime: 10 * 60_000,
  });
}

/**
 * Create new API key
 *
 * Uses project header to create API key in the selected project.
 */
export function useCreateApiKey() {
  const queryCache = useQueryCache();
  const projectStore = useProjectStore();

  return useMutation({
    mutation: async (payload: ApiKeyBody) => {
      return await apiKeysApi.create(payload, {
        headers: projectStore.getProjectHeader(),
      });
    },
    onSuccess: () => {
      queryCache.invalidateQueries({ key: API_KEYS_QUERY_KEYS.root as any });
      queryCache.invalidateQueries({ key: SUBSCRIPTION_LIMITS_QUERY_KEYS.root as any });
    },
  });
}

/**
 * Update existing API key
 *
 * Uses project header for access control.
 */
export function useUpdateApiKey() {
  const queryCache = useQueryCache();
  const projectStore = useProjectStore();

  return useMutation({
    mutation: async (payload: { id: string } & ApiKeyBody) => {
      const { id, ...body } = payload;
      return await apiKeysApi.updateById(id, body, {
        headers: projectStore.getProjectHeader(),
      });
    },
    onSuccess: () => {
      queryCache.invalidateQueries({ key: API_KEYS_QUERY_KEYS.root as any });
    },
  });
}

/**
 * Rotate an API key credential.
 *
 * The new plaintext is returned once by the response; callers must reveal it
 * immediately and never keep it in query or list cache.
 */
export function useRotateApiKey() {
  const queryCache = useQueryCache();
  const projectStore = useProjectStore();

  return useMutation({
    mutation: async (payload: { id: string } & RotateApiKeyBody) => {
      return await apiKeysApi.rotate(payload, {
        headers: projectStore.getProjectHeader(),
      });
    },
    onSuccess: () => {
      queryCache.invalidateQueries({ key: API_KEYS_QUERY_KEYS.root as any });
    },
  });
}

/**
 * End a rotation grace period early.
 *
 * The previous credential is invalidated immediately; no new secret is issued.
 */
export function useFinishRotation() {
  const queryCache = useQueryCache();
  const projectStore = useProjectStore();

  return useMutation({
    mutation: async (id: string) => {
      return await apiKeysApi.finishRotation(id, {
        headers: projectStore.getProjectHeader(),
      });
    },
    onSuccess: () => {
      queryCache.invalidateQueries({ key: API_KEYS_QUERY_KEYS.root as any });
    },
  });
}

/**
 * Delete API key
 *
 * Uses project header for access control.
 */
export function useDeleteApiKey() {
  const queryCache = useQueryCache();
  const projectStore = useProjectStore();

  return useMutation({
    mutation: async (id: string) => {
      return await apiKeysApi.deleteById(id, {
        headers: projectStore.getProjectHeader(),
      });
    },
    onSuccess: () => {
      queryCache.invalidateQueries({ key: API_KEYS_QUERY_KEYS.root as any });
      queryCache.invalidateQueries({ key: SUBSCRIPTION_LIMITS_QUERY_KEYS.root as any });
    },
  });
}

/**
 * Query hook for fetching API keys for a specific user (admin only)
 */
export function useApiKeysForUserQuery(userId: MaybeRefOrGetter<string>) {
  const projectStore = useProjectStore();

  return useQuery({
    key: () =>
      API_KEYS_QUERY_KEYS.listForUser(
        toValue(userId),
        projectStore.currentProjectId,
      ) as unknown as EntryKey,
    query: async () => {
      return await apiKeysApi.getForUser(toValue(userId), {
        headers: projectStore.getProjectHeader(),
      });
    },
    enabled: () => !!toValue(userId),
    staleTime: 5 * 60_000,
    gcTime: 10 * 60_000,
  });
}

/**
 * Create API key on behalf of another user (admin only)
 */
export function useCreateApiKeyForUser() {
  const queryCache = useQueryCache();
  const projectStore = useProjectStore();

  return useMutation({
    mutation: async (payload: { userId: string; body: ApiKeyBody }) => {
      return await apiKeysApi.createForUser(payload.userId, payload.body, {
        headers: projectStore.getProjectHeader(),
      });
    },
    onSuccess: () => {
      queryCache.invalidateQueries({ key: API_KEYS_QUERY_KEYS.root as any });
      queryCache.invalidateQueries({ key: SUBSCRIPTION_LIMITS_QUERY_KEYS.root as any });
    },
  });
}

/**
 * Query hook for fetching API key usage summary
 *
 * Features:
 * - Smart caching with 5min stale time
 * - Automatic refetch on window focus
 * - Only runs if API key ID exists
 *
 * @param id - API Key ID (can be reactive)
 * @param params - Query parameters (period, dates)
 *
 * @example
 * ```ts
 * const apiKeyId = ref('abc-123');
 * const params = computed(() => ({ period_days: 30 }));
 * const { data, isLoading, error } = useApiKeyUsageSummaryQuery(apiKeyId, params);
 * ```
 */
export function useApiKeyUsageSummaryByIdQuery(
  id: MaybeRefOrGetter<string>,
  params?: MaybeRefOrGetter<UsageSummaryParams>
) {
  return useQuery({
    key: () =>
      API_KEYS_QUERY_KEYS.usageSummaryById(toValue(id), toValue(params)) as unknown as EntryKey,
    query: async () => {
      const idValue = toValue(id);
      const paramsValue = toValue(params);
      return await apiKeysApi.getUsageSummaryById(idValue, paramsValue);
    },
    enabled: () => !!toValue(id),
    staleTime: 5 * 60_000, // 5 minutes - costs don't change frequently
    gcTime: 10 * 60_000, // 10 minutes - keep in cache
  });
}

type ApiKeyUsageSummaryReturn = UseQueryReturn<
  Awaited<ReturnType<typeof apiKeysApi.getUsageSummary>>,
  Error
> & { filter: Ref<UsageSummaryParams> };

export const useApiKeyUsageSummaryQuery = defineQuery((): ApiKeyUsageSummaryReturn => {
  const now = new Date();
  const thirtyDaysAgo = new Date(now);
  thirtyDaysAgo.setDate(now.getDate() - 30);

  const filter = ref<UsageSummaryParams>({
    start_date: thirtyDaysAgo.toISOString(),
    end_date: now.toISOString(),
  });

  const query = useQuery({
    key: () => API_KEYS_QUERY_KEYS.usageSummary(toValue(filter)) as unknown as EntryKey,
    query: async () => {
      return await apiKeysApi.getUsageSummary(toValue(filter));
    },

    staleTime: 5 * 60_000, // 5 minutes - costs don't change frequently
    gcTime: 10 * 60_000, // 10 minutes - keep in cache
  });

  return {
    ...query,
    filter,
  };
});

/**
 * Query hook for fetching paginated API key usage details
 *
 * Features:
 * - Paginated data with placeholderData for smooth transitions
 * - Filterable by date range, charge types
 * - Sortable by various fields
 *
 * @param id - API Key ID (can be reactive)
 * @param filter - Filter parameters (pagination, date range, types)
 *
 * @example
 * ```ts
 * const apiKeyId = ref('abc-123');
 * const filter = computed(() => ({
 *   page: 1,
 *   pageSize: 50,
 *   sort_by: 'event_timestamp',
 *   sort_order: 'desc'
 * }));
 * const { data, isLoading } = useApiKeyUsageDetailsQuery(apiKeyId, filter);
 * ```
 */
export function useApiKeyUsageDetailsByIdQuery(
  id: MaybeRefOrGetter<string>,
  filter?: MaybeRefOrGetter<UsageDetailsFilter>
) {
  return useQuery({
    key: () =>
      API_KEYS_QUERY_KEYS.usageDetails(toValue(id), toValue(filter)) as unknown as EntryKey,
    query: async () => {
      const idValue = toValue(id);
      const filterValue = toValue(filter);
      return await apiKeysApi.getUsageDetailsById(idValue, filterValue);
    },
    enabled: () => !!toValue(id),
    // Keep previous data while loading next page (prevents flicker)
    placeholderData: (previousData) => previousData,
    staleTime: 30_000, // 30 seconds
    gcTime: 5 * 60_000, // 5 minutes
  });
}

/**
 * Query hook for fetching aggregated API key usage data
 *
 * Features:
 * - Aggregated by day/week/month intervals
 * - Used for charts and visualizations
 * - Cached for 5 minutes
 *
 * @param id - API Key ID (can be reactive)
 * @param params - Aggregation parameters (interval, date range)
 *
 * @example
 * ```ts
 * const apiKeyId = ref('abc-123');
 * const params = computed(() => ({
 *   interval: 'day',
 *   start_date: '2024-01-01',
 *   end_date: '2024-01-31'
 * }));
 * const { data, isLoading } = useApiKeyUsageAggregatedQuery(apiKeyId, params);
 * ```
 */
export function useApiKeyUsageAggregatedQuery(
  id: MaybeRefOrGetter<string>,
  params?: MaybeRefOrGetter<UsageAggregatedParams>
) {
  return useQuery({
    key: () =>
      API_KEYS_QUERY_KEYS.usageAggregated(toValue(id), toValue(params)) as unknown as EntryKey,
    query: async () => {
      const idValue = toValue(id);
      const paramsValue = toValue(params);
      return await apiKeysApi.getUsageAggregated(idValue, paramsValue);
    },
    enabled: () => !!toValue(id),
    staleTime: 5 * 60_000, // 5 minutes - aggregated data doesn't change frequently
    gcTime: 10 * 60_000, // 10 minutes
  });
}

/**
 * Helper hook to invalidate API key usage queries
 *
 * Use this after operations that affect usage data
 *
 * @example
 * ```ts
 * const invalidate = useInvalidateApiKeyUsage();
 *
 * // After some mutation
 * invalidate.all('api-key-id'); // Invalidate all usage queries for this key
 * ```
 */
export function useInvalidateApiKeyUsage() {
  const queryCache = useQueryCache();

  return {
    /**
     * Invalidate all usage queries for a specific API key
     */
    all: (apiKeyId: string) =>
      queryCache.invalidateQueries({
        key: [...API_KEYS_QUERY_KEYS.root, apiKeyId, "usage"] as any,
      }),

    /**
     * Invalidate only summary query for a specific API key
     */
    summaryById: (apiKeyId: string, params?: UsageSummaryParams) =>
      queryCache.invalidateQueries({
        key: API_KEYS_QUERY_KEYS.usageSummaryById(apiKeyId, params) as any,
      }),
    /**
     * Invalidate only summary query for a specific API key
     */
    summary: (params?: UsageSummaryParams) =>
      queryCache.invalidateQueries({
        key: API_KEYS_QUERY_KEYS.usageSummary(params) as any,
      }),

    /**
     * Invalidate only details query for a specific API key
     */
    details: (apiKeyId: string, filter?: UsageDetailsFilter) =>
      queryCache.invalidateQueries({
        key: API_KEYS_QUERY_KEYS.usageDetails(apiKeyId, filter) as any,
      }),

    /**
     * Invalidate only aggregated query for a specific API key
     */
    aggregated: (apiKeyId: string, params?: UsageAggregatedParams) =>
      queryCache.invalidateQueries({
        key: API_KEYS_QUERY_KEYS.usageAggregated(apiKeyId, params) as any,
      }),
  };
}
